import { useState } from "react";
import { SendIcon } from "lucide-react";
import type { JSONContent } from "@tiptap/react";

import { MentionTextInput } from "@/components";
import { Button } from "@/components/ui/button";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { useCreateInternalCandidacyComment } from "@/hooks/mutations/useCreateInternalCandidacyComment";
import { useSetNewInternalCandidacyCommentInInternalCandidacyComments } from "@/hooks/sets/useSetNewInternalCandidacyCommentInInternalCandidacyComments";
import { tiptapDefaultJson } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";

// Thread-reply composer. The draft is deliberately local — unlike the
// top-level composers, it doesn't survive scope/tab switches.
export const ReplyComposer = ({
  candidacyId,
  onSent,
  threadId,
}: {
  candidacyId: string;
  onSent: () => void;
  threadId: number;
}) => {
  /* Database CRUD Operations */
  // Create
  const { isPending, mutate: addComment } = useCreateInternalCandidacyComment();

  /* Cache Updates */
  // Set
  const setNewCommentInComments =
    useSetNewInternalCandidacyCommentInInternalCandidacyComments();

  /* React Hooks */
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const [replyDraft, setReplyDraft] = useState<JSONContent>(tiptapDefaultJson);

  /* Helpers */
  const canSend =
    !isPending &&
    JSON.stringify(replyDraft.content) !==
      JSON.stringify(tiptapDefaultJson.content);

  /* Handlers */
  const handleSend = () => {
    if (!canSend) return;

    mixpanelUtils.trackFormSubmission(
      "Add reply",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    addComment(
      {
        candidacyId,
        createInternalCandidacyCommentRequest: {
          contentJson: replyDraft,
          threadId,
        },
        currentCompanyId,
        currentJobId,
      },
      {
        onSuccess: (newComment) => {
          setReplyDraft(tiptapDefaultJson);
          setNewCommentInComments({
            candidacyId,
            internalCandidacyCommentToAdd: newComment,
          });
          onSent();
        },
      },
    );
  };

  /* Render */
  const render = () => {
    return (
      <div className="relative pt-1.5">
        <MentionTextInput
          autoFocus
          className="border-slate-300/70 bg-primary-50 pr-11"
          onChange={setReplyDraft}
          onSubmit={handleSend}
          placeholder="Write a reply..."
          value={replyDraft}
        />
        <Button
          className="absolute bottom-1 right-1 h-8 w-8 text-primary-900 hover:bg-primary-100 hover:text-primary-900"
          disabled={!canSend}
          onClick={handleSend}
          onMouseDown={(e) => {
            // Keep keyboard focus in the input when clicking send.
            e.preventDefault();
          }}
          size="icon"
          variant="ghost"
        >
          <SendIcon className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  return render();
};
